import { BookingWindow, RollingLinkRange, RollingLinkNoticeKind } from "@/api/types";

/**
 * A session-scoped autosave of the new-booking-page builder. It exists so that a
 * click that navigates away (e.g. opening a confirmed meeting on the Meetings
 * calendar) — or a reload — never throws away half-written work: the builder
 * refills itself on return. Stored in `sessionStorage`, so it survives navigation
 * and tab reloads but is cleared when the browser closes (nothing lingers).
 *
 * Only the NEW-page builder autosaves; a created link is immutable, so there's no
 * draft to keep for it.
 */
export type BuilderDraft = {
  title: string;
  description: string;
  slotMinutes: number;
  maxBookings: string;
  maxTouched: boolean;
  oneEach: boolean;
  allowCancel: boolean;
  validFrom: string;
  // No validUntil: when a link closes is derived server-side, so there is nothing here to restore.
  // A draft saved before that change simply carries an extra key, which is ignored on load.
  windows: BookingWindow[];
  filledTypeId: string;
  conferencingProvider: string;
  physicalOn: boolean;
  location: string;
  typedDate: string;
  typedDateEnd: string;
  typedFrom: string;
  typedTo: string;
  showMine: boolean;
};

const DRAFT_KEY = "scheda.bookings.draft.new.v1";

/** True when the draft holds work worth telling the user we restored. */
export function draftHasContent(d: BuilderDraft | null): boolean {
  if (!d) return false;
  return d.title.trim().length > 0 || d.description.trim().length > 0 || d.windows.length > 0;
}

export function loadDraft(): BuilderDraft | null {
  try {
    const raw = window.sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<BuilderDraft>;
    // Guard the one field the UI iterates over; the rest fall back at read time.
    if (!Array.isArray(d.windows)) d.windows = [];
    return d as BuilderDraft;
  } catch {
    return null;
  }
}

export function saveDraft(draft: BuilderDraft): void {
  try {
    window.sessionStorage.setItem(DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* private mode / quota — autosave just won't persist */
  }
}

export function clearDraft(): void {
  try {
    window.sessionStorage.removeItem(DRAFT_KEY);
  } catch {
    /* nothing to do */
  }
}

/* ---------------------------------------------------------------------------
   Rolling-appointment (rollingLink) builder draft — same idea as the ad-hoc draft
   above, for the NEW-rollingLink builder: leaving mid-build (or a reload) comes back
   to exactly this state, and the builder shows a "Draft restored" chip.
--------------------------------------------------------------------------- */
export type RollingLinkBuilderDraft = {
  title: string;
  description: string;
  slotMinutes: number;
  gapMinutes: number;
  noticeKind: RollingLinkNoticeKind;
  noticeMinutes: number;
  provider: string;
  physicalOn: boolean;
  location: string;
  ranges: RollingLinkRange[];
  filledTypeId: string;
};

const CLINIC_DRAFT_KEY = "scheda.rollingLinks.draft.new.v1";

/** True when the rolling link draft holds work worth telling the user we restored. */
export function rollingLinkDraftHasContent(d: RollingLinkBuilderDraft | null): boolean {
  if (!d) return false;
  return d.title.trim().length > 0 || d.description.trim().length > 0 || d.ranges.length > 0;
}

export function loadRollingLinkDraft(): RollingLinkBuilderDraft | null {
  try {
    const raw = window.sessionStorage.getItem(CLINIC_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<RollingLinkBuilderDraft>;
    if (!Array.isArray(d.ranges)) d.ranges = [];
    return d as RollingLinkBuilderDraft;
  } catch {
    return null;
  }
}

export function saveRollingLinkDraft(draft: RollingLinkBuilderDraft): void {
  try {
    window.sessionStorage.setItem(CLINIC_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* private mode / quota — autosave just won't persist */
  }
}

export function clearRollingLinkDraft(): void {
  try {
    window.sessionStorage.removeItem(CLINIC_DRAFT_KEY);
  } catch {
    /* nothing to do */
  }
}

/* ---------------------------------------------------------------------------
   Poll builder draft — the same idea again, for the NEW-poll builder. Kept as a
   third parallel block rather than folded into a factory: the three drafts hold
   genuinely different shapes, and one generic version would be harder to read
   than three obvious ones.
--------------------------------------------------------------------------- */
export type PollBuilderDraft = {
  title: string;
  description: string;
  durationMinutes: number;
  provider: string;
  physicalOn: boolean;
  location: string;
  respondBy: string;
  showCounts: boolean;
  /** The proposed times, as dated windows (start + start + duration). */
  windows: BookingWindow[];
  showMine: boolean;
};

const POLL_DRAFT_KEY = "scheda.polls.draft.new.v1";

/** True when the poll draft holds work worth telling the user we restored. */
export function pollDraftHasContent(d: PollBuilderDraft | null): boolean {
  if (!d) return false;
  return d.title.trim().length > 0 || d.description.trim().length > 0 || d.windows.length > 0;
}

export function loadPollDraft(): PollBuilderDraft | null {
  try {
    const raw = window.sessionStorage.getItem(POLL_DRAFT_KEY);
    if (!raw) return null;
    const d = JSON.parse(raw) as Partial<PollBuilderDraft>;
    if (!Array.isArray(d.windows)) d.windows = [];
    return d as PollBuilderDraft;
  } catch {
    return null;
  }
}

export function savePollDraft(draft: PollBuilderDraft): void {
  try {
    window.sessionStorage.setItem(POLL_DRAFT_KEY, JSON.stringify(draft));
  } catch {
    /* private mode / quota — autosave just won't persist */
  }
}

export function clearPollDraft(): void {
  try {
    window.sessionStorage.removeItem(POLL_DRAFT_KEY);
  } catch {
    /* nothing to do */
  }
}
